import { Link } from 'react-router-dom';
import { 
  Facebook, 
  Instagram, 
  Mail, 
  Phone, 
  MapPin,
  ArrowRight,
  Share2,
  Cookie
} from 'lucide-react';
import logo from '@/assets/logo.avif';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useCookieConsentContext } from '@/contexts/CookieConsentContext';

// Custom TikTok icon (lucide doesn't have one)
const TikTokIcon = ({ className }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    className={className}
  >
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

const keyServiceAreas = [
  { name: 'Cardiff', slug: 'cardiff' },
  { name: 'Swansea', slug: 'swansea' },
  { name: 'Newport', slug: 'newport' },
  { name: 'Bridgend', slug: 'bridgend' },
  { name: 'Neath Port Talbot', slug: 'neath' },
  { name: 'Porthcawl', slug: 'porthcawl' },
];

const services = [
  { name: 'Web Design', slug: '/services' },
  { name: 'Local SEO', slug: '/services' },
  { name: 'Content Writing', slug: '/services' },
  { name: 'AI Schema', slug: '/services' },
  { name: 'Care Plans', slug: '/services' },
];

const quickLinks = [
  { name: 'Portfolio', slug: '/portfolio' },
  { name: 'About Us', slug: '/about' },
  { name: 'Insights Blog', slug: '/insights' },
  { name: 'Resources', slug: '/resources' },
  { name: 'Contact', slug: '/contact' },
  { name: 'Privacy Policy', slug: '/privacy' },
  { name: 'Terms of Service', slug: '/terms' },
];

const socialLinks = [
  { icon: Facebook, href: 'https://www.facebook.com/people/SmartSite-Solutions/61576469503515/', label: 'Facebook' },
  { icon: Instagram, href: 'https://www.instagram.com/smartsitesolutions.co.uk/', label: 'Instagram' },
  { icon: TikTokIcon, href: 'https://www.tiktok.com/@smartsitesolutions', label: 'TikTok' },
];

export function Footer() {
  const { openPreferences } = useCookieConsentContext();
  
  const handleShare = async () => {
    const shareData = {
      title: 'SmartSite Solutions',
      text: 'High-performance websites for South Wales trades',
      url: window.location.origin,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.origin);
        toast.success('Link copied to clipboard!');
      }
    } catch (error) {
      // User cancelled or error occurred
      if ((error as Error).name !== 'AbortError') {
        await navigator.clipboard.writeText(window.location.origin);
        toast.success('Link copied to clipboard!');
      }
    }
  };

  return (
    <footer role="contentinfo" aria-label="Site footer" className="relative overflow-hidden pb-32">
      {/* Main Footer */}
      <div className="border-t border-white/10 py-16 lg:py-20">
        <div className="container mx-auto px-6 lg:px-8">
          {/* Mobile: Brand + Contact first, then 3-column links */}
          <div className="flex flex-col gap-12">
            {/* Brand & Contact Section */}
            <div className="space-y-6 text-left">
              {/* Logo */}
              <Link to="/" className="flex items-center gap-3 group">
                <img 
                  src={logo} 
                  alt="SmartSite Solutions" 
                  className="w-10 h-10 rounded-xl object-cover"
                />
              </Link>
              
              <p className="text-muted-foreground max-w-sm">
                High-performance websites for South Wales trades. 
                We help plumbers, builders, and tradespeople get more leads with fast, SEO-optimised websites.
              </p>
              
              {/* Contact Info - NAP */}
              <div className="space-y-3">
                <a href="tel:+447852872088" className="flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors">
                  <Phone className="w-4 h-4 text-orange-400" />
                  <span>07852 872 088</span>
                </a>
                <a href="mailto:smartsitesolutions@pm.me" className="flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors">
                  <Mail className="w-4 h-4 text-orange-400" />
                  <span>smartsitesolutions@pm.me</span>
                </a>
                <div className="flex items-start gap-3 text-muted-foreground">
                  <MapPin className="w-4 h-4 text-orange-400 mt-0.5" />
                  <span>North Cornelly, South Wales, UK</span>
                </div>
              </div>
              
              {/* Social Links */}
              <div className="flex gap-3">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center hover:bg-orange-500/20 hover:border-orange-500/50 hover:text-orange-400 hover:shadow-[0_0_15px_rgba(249,115,22,0.3)] transition-all duration-300"
                  >
                    <social.icon className="w-4 h-4" />
                  </a>
                ))}
                {/* Share Button */}
                <button
                  onClick={handleShare}
                  aria-label="Share this website"
                  className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center hover:bg-orange-500/20 hover:border-orange-500/50 hover:text-orange-400 hover:shadow-[0_0_15px_rgba(249,115,22,0.3)] transition-all duration-300"
                >
                  <Share2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            {/* 3-Column Links Grid */}
            <div className="grid grid-cols-3 gap-4 md:gap-6 text-left">
              {/* Links */}
              <div>
                <h4 className="font-display font-semibold mb-3 md:mb-4 text-xs md:text-sm">Links</h4>
                <ul className="space-y-2.5">
                  {quickLinks.map((link) => (
                    <li key={link.name}>
                      <Link 
                        to={link.slug}
                        className="text-muted-foreground hover:text-foreground transition-colors text-xs"
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            
              {/* Services */}
              <div>
                <h4 className="font-display font-semibold mb-3 md:mb-4 text-xs md:text-sm">Services</h4>
                <ul className="space-y-2.5">
                  {services.map((service) => (
                    <li key={service.name}>
                      <Link 
                        to={service.slug}
                        className="text-muted-foreground hover:text-foreground transition-colors text-xs"
                      >
                        {service.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            
              {/* Service Areas */}
              <div>
                <h4 className="font-display font-semibold mb-3 md:mb-4 text-xs md:text-sm">Service Areas</h4>
                <ul className="space-y-2.5">
                  {keyServiceAreas.map((area) => (
                    <li key={area.slug}>
                      <Link 
                        to={`/locations/${area.slug}`}
                        className="text-xs text-muted-foreground hover:text-orange-500 transition-colors"
                      >
                        {area.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Copyright Bar */}
      <div className="border-t border-white/5 py-6">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-start md:items-center md:justify-center gap-4 text-sm text-slate-400">
            <p>© 2026 SmartSite Solutions. Engineered in North Cornelly.</p>
            <span className="hidden md:inline">·</span>
            <button
              onClick={openPreferences}
              className="flex items-center gap-1.5 text-slate-400 hover:text-orange-400 transition-colors"
              aria-label="Manage cookie settings"
            >
              <Cookie className="w-4 h-4" />
              Cookie Settings
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}